import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { branchesAPI, posAPI, getApiErrorPayload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface BranchLite {
	id: number;
	name: string;
}
interface PosLite {
	id: number;
	name: string;
	code?: string;
	branchId?: number | null;
}

export interface EmployeeBranchPosSectionProps {
	branches: BranchLite[];
	branchId?: number;
	onBranchChange: (branchId: number | undefined) => void;
	onBranchesRefresh: (selectId?: number) => void;
	posIds: number[];
	onPosIdsChange: (ids: number[]) => void;
	defaultPosId?: number | null;
	onDefaultPosChange: (id: number | null) => void;
	disabled?: boolean;
}

/**
 * Branch + POS assignment for the employee form. Supports inline creation of a branch and of a
 * POS point under the selected branch, refreshing selections immediately, and prevents assigning
 * POS terminals that do not belong to the chosen branch.
 */
export const EmployeeBranchPosSection: React.FC<EmployeeBranchPosSectionProps> = ({
	branches,
	branchId,
	onBranchChange,
	onBranchesRefresh,
	posIds,
	onPosIdsChange,
	defaultPosId,
	onDefaultPosChange,
	disabled = false,
}) => {
	const { t } = useTranslation();
	const { toast } = useToast();

	const [posList, setPosList] = useState<PosLite[]>([]);
	const [loadingPos, setLoadingPos] = useState(false);

	const [branchDialogOpen, setBranchDialogOpen] = useState(false);
	const [newBranchName, setNewBranchName] = useState("");
	const [newBranchNameEn, setNewBranchNameEn] = useState("");
	const [creatingBranch, setCreatingBranch] = useState(false);

	const [posDialogOpen, setPosDialogOpen] = useState(false);
	const [newPosName, setNewPosName] = useState("");
	const [creatingPos, setCreatingPos] = useState(false);

	const loadPos = useCallback(async (bId: number) => {
		try {
			setLoadingPos(true);
			const res = await posAPI.getAll({ branchId: bId, limit: 100, page: 1 });
			setPosList(res?.pos ?? []);
		} catch {
			setPosList([]);
		} finally {
			setLoadingPos(false);
		}
	}, []);

	useEffect(() => {
		if (branchId == null) {
			setPosList([]);
			return;
		}
		loadPos(branchId);
	}, [branchId, loadPos]);

	const handleCreateBranch = async () => {
		if (!newBranchName.trim() || !newBranchNameEn.trim()) {
			toast({
				title: t("common.error"),
				description: t("users.form.branchNameRequired"),
				variant: "destructive",
			});
			return;
		}
		try {
			setCreatingBranch(true);
			const created = await branchesAPI.create({
				name: newBranchName.trim(),
				nameEn: newBranchNameEn.trim(),
			});
			toast({ title: t("common.success"), description: t("users.form.branchCreated") });
			setBranchDialogOpen(false);
			setNewBranchName("");
			setNewBranchNameEn("");
			onBranchesRefresh(created?.id);
			if (created?.id) {
				onBranchChange(created.id);
				onPosIdsChange([]);
				onDefaultPosChange(null);
			}
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setCreatingBranch(false);
		}
	};

	const handleCreatePos = async () => {
		if (branchId == null) return;
		if (!newPosName.trim()) {
			toast({
				title: t("common.error"),
				description: t("users.form.posNameRequired"),
				variant: "destructive",
			});
			return;
		}
		try {
			setCreatingPos(true);
			const created = await posAPI.create({ name: newPosName.trim(), branchId });
			toast({ title: t("common.success"), description: t("users.form.posCreated") });
			setPosDialogOpen(false);
			setNewPosName("");
			await loadPos(branchId);
			if (created?.id) onPosIdsChange(Array.from(new Set([...posIds, Number(created.id)])));
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setCreatingPos(false);
		}
	};

	const togglePos = (posId: number, checked: boolean) => {
		const next = checked ? Array.from(new Set([...posIds, posId])) : posIds.filter(id => id !== posId);
		onPosIdsChange(next);
		if (!checked && defaultPosId === posId) onDefaultPosChange(null);
	};

	return (
		<>
			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<Label htmlFor="branchId">{t("users.form.branch")}</Label>
					{!disabled && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="h-7 gap-1 text-xs"
							onClick={() => setBranchDialogOpen(true)}
						>
							<Plus className="h-3.5 w-3.5" />
							{t("users.form.addBranch")}
						</Button>
					)}
				</div>
				<Select
					value={branchId != null ? String(branchId) : ""}
					onValueChange={value => {
						const next = value ? Number(value) : undefined;
						onBranchChange(next);
						onPosIdsChange([]);
						onDefaultPosChange(null);
					}}
					disabled={disabled}
				>
					<SelectTrigger aria-label={t("users.form.branch")} className={disabled ? "bg-muted" : ""}>
						<SelectValue placeholder={t("users.form.selectBranch")} />
					</SelectTrigger>
					<SelectContent>
						{branches.map(branch => (
							<SelectItem key={branch.id} value={String(branch.id)}>
								{branch.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<p className="text-xs text-muted-foreground">{t("users.form.branchIsDefault")}</p>
			</div>

			{branchId != null && (
				<div className="space-y-2 md:col-span-2">
					<div className="flex items-center justify-between">
						<Label>{t("users.form.posPoints")}</Label>
						{!disabled && (
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-7 gap-1 text-xs"
								onClick={() => setPosDialogOpen(true)}
							>
								<Plus className="h-3.5 w-3.5" />
								{t("users.form.addPos")}
							</Button>
						)}
					</div>
					<p className="text-xs text-muted-foreground">{t("users.form.posPointsHint")}</p>
					<div className="rounded-md border p-3 max-h-56 overflow-y-auto space-y-2">
						{loadingPos ? (
							<p className="text-sm text-muted-foreground">{t("common.loading")}</p>
						) : posList.length === 0 ? (
							<p className="text-sm text-muted-foreground">{t("users.form.noPosForBranch")}</p>
						) : (
							posList.map(pos => (
								<label key={pos.id} className="flex items-center gap-2 text-sm cursor-pointer">
									<Checkbox
										checked={posIds.includes(pos.id)}
										onCheckedChange={c => togglePos(pos.id, c === true)}
										disabled={disabled}
									/>
									<span>
										{pos.name}
										{pos.code ? ` (${pos.code})` : ""}
									</span>
								</label>
							))
						)}
					</div>

					{posIds.length > 0 && (
						<div className="space-y-2">
							<Label>{t("users.form.defaultPos")}</Label>
							<Select
								value={defaultPosId != null ? String(defaultPosId) : "__none__"}
								onValueChange={v => onDefaultPosChange(v === "__none__" ? null : Number(v))}
								disabled={disabled}
							>
								<SelectTrigger
									aria-label={t("users.form.defaultPos")}
									className={disabled ? "bg-muted" : ""}
								>
									<SelectValue placeholder={t("users.form.selectDefaultPos")} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="__none__">{t("users.form.noDefaultPos")}</SelectItem>
									{posList
										.filter(pos => posIds.includes(pos.id))
										.map(pos => (
											<SelectItem key={pos.id} value={String(pos.id)}>
												{pos.name}
												{pos.code ? ` (${pos.code})` : ""}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
						</div>
					)}
				</div>
			)}

			{/* Inline branch creation */}
			<Dialog open={branchDialogOpen} onOpenChange={setBranchDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("users.form.addBranch")}</DialogTitle>
						<DialogDescription>{t("users.form.addBranchHint")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label>{t("users.form.branchNameAr")}</Label>
							<Input
								aria-label={t("users.form.branchNameAr")}
								value={newBranchName}
								onChange={e => setNewBranchName(e.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("users.form.branchNameEn")}</Label>
							<Input
								aria-label={t("users.form.branchNameEn")}
								value={newBranchNameEn}
								onChange={e => setNewBranchNameEn(e.target.value)}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => setBranchDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button type="button" onClick={handleCreateBranch} disabled={creatingBranch}>
							{t("common.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Inline POS creation under the selected branch */}
			<Dialog open={posDialogOpen} onOpenChange={setPosDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("users.form.addPos")}</DialogTitle>
						<DialogDescription>{t("users.form.addPosHint")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label>{t("users.form.posName")}</Label>
							<Input
								aria-label={t("users.form.posName")}
								value={newPosName}
								onChange={e => setNewPosName(e.target.value)}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => setPosDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button type="button" onClick={handleCreatePos} disabled={creatingPos}>
							{t("common.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default EmployeeBranchPosSection;
